import type {
  CampaignReportResponse,
  CampaignReportType,
  ComparisonReportResponse,
  HessReportResponse,
  LinearReportResponse,
  SocialCampaignReportResponse,
  StreamingReportResponse,
} from "../types";
import { log } from "../utils/logger";
import { buildApiUrl, postMultipart } from "./transport";

/**
 * Upload a single campaign/social report file for processing.
 *
 * @param file - CSV or Excel file to upload
 * @param reportType - Upload report family: search, onsite, offsite, or social
 * @param retailer - Retailer identifier selected from hydrated upload config
 * @param client - Client identifier selected from hydrated upload config
 * @param channel - Channel identifier selected from retailer configuration
 * @param product - Product identifier for product-specific processing (e.g., "gyoza" for enhanced format)
 * @returns Processed campaign or social dashboard payload with aggregated metrics
 */
export interface CampaignUploadRequest {
  file: File;
  reportType: CampaignReportType | "social";
  retailer: string;
  client: string;
  channel?: string;
  product?: string;
}

export async function uploadCampaignReportRequest({
  file,
  reportType,
  retailer,
  client,
  channel,
  product,
}: CampaignUploadRequest): Promise<
  CampaignReportResponse | SocialCampaignReportResponse
> {
  const url = buildApiUrl("/upload/report");

  log.info(
    "Uploading campaign report",
    {
      fileName: file.name,
      fileSizeKb: Number((file.size / 1024).toFixed(2)),
      reportType,
      retailer,
      client,
      channel: channel || reportType,
      product: product || "all",
      url,
    },
    "api",
  );

  const { data } = await postMultipart<
    CampaignReportResponse | SocialCampaignReportResponse
  >({
    url,
    fields: {
      file,
      report_type: reportType,
      retailer,
      client,
      channel,
      product,
    },
    onError: (response, errorMessage) => {
      log.error("Campaign report upload failed", {
        status: response.status,
        errorMessage,
      }, "api");
    },
  });

  log.info(
    "Campaign report uploaded and processed successfully",
    {
      reportType: data.report_type,
      recordCount: data.records_processed,
      campaignCount: data.summary.campaign_count,
    },
    "api",
  );

  return data;
}

export function uploadCampaignReport(
  file: File,
  reportType: CampaignReportType | "social",
  retailer: string,
  client: string,
  channel?: string,
  product?: string,
): Promise<CampaignReportResponse | SocialCampaignReportResponse> {
  return uploadCampaignReportRequest({
    file,
    reportType,
    retailer,
    client,
    channel,
    product,
  });
}

/**
 * Upload two reports for the same retailer/channel to get period-over-period comparison.
 *
 * @param currentFile - The more recent report file
 * @param priorFile - The earlier/baseline report file
 * @param reportType - Type of report (currently only 'search')
 * @param retailer - Retailer identifier
 * @param client - Client identifier
 * @param channel - Channel identifier
 * @param product - Product identifier for product-specific comparison processing
 * @returns Comparison data with metric deltas and directional indicators
 */
export interface ComparisonUploadRequest {
  currentFile: File;
  priorFile: File;
  reportType?: "search";
  retailer?: string;
  client?: string;
  channel?: string;
  product?: string;
}

export async function uploadComparisonReportRequest({
  currentFile,
  priorFile,
  reportType = "search",
  retailer = "walmart",
  client = "ajinomoto",
  channel,
  product,
}: ComparisonUploadRequest): Promise<ComparisonReportResponse> {
  const url = buildApiUrl("/upload/compare");

  log.info(
    "Uploading comparison reports",
    {
      currentFile: currentFile.name,
      priorFile: priorFile.name,
      reportType,
      retailer,
      client,
      channel,
      product,
      url,
    },
    "api",
  );

  const { data } = await postMultipart<ComparisonReportResponse>({
    url,
    fields: {
      current_file: currentFile,
      prior_file: priorFile,
      report_type: reportType,
      retailer,
      client,
      channel,
      product,
    },
    onError: (response, errorMessage) => {
      log.error(
        "Comparison upload failed",
        { status: response.status, errorMessage },
        "api",
      );
    },
  });

  log.info(
    "Comparison upload successful",
    {
      metricsCount: data.comparison.metrics.length,
      retailer: data.retailer,
    },
    "api",
  );

  return data;
}

export function uploadComparisonReport(
  currentFile: File,
  priorFile: File,
  reportType: "search" = "search",
  retailer: string = "walmart",
  client: string = "ajinomoto",
  channel?: string,
  product?: string,
): Promise<ComparisonReportResponse> {
  return uploadComparisonReportRequest({
    currentFile,
    priorFile,
    reportType,
    retailer,
    client,
    channel,
    product,
  });
}

/**
 * Upload multiple Linear TV channel reports for processing.
 *
 * @param files - Array of Linear TV Excel files accepted by the selected descriptor
 * @returns Processed Linear TV data with aggregated metrics and channel breakdowns
 */
export async function uploadLinearReports(
  files: File[],
): Promise<LinearReportResponse> {
  const url = buildApiUrl("/upload/linear");

  log.info(
    "Uploading Linear TV reports",
    {
      fileNames: files.map((f) => f.name),
      fileCount: files.length,
      totalSizeKb: Number(
        (files.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(2),
      ),
      url,
    },
    "api",
  );

  const { data } = await postMultipart<LinearReportResponse>({
    url,
    fields: { files },
    onError: (response, errorMessage) => {
      log.error(
        "Linear TV upload failed",
        { status: response.status, errorMessage },
        "api",
      );
    },
  });

  log.info(
    "Linear TV reports uploaded and processed successfully",
    {
      recordCount: data.records_processed,
      channelCount: data.summary.channel_count,
      channels: data.summary.channels,
      totalSpend: data.summary.total_spend,
    },
    "api",
  );

  return data;
}

/**
 * Upload MNTN Streaming TV reports (4 CSV files: Segments + KPI Totals + Visits by DMA + Visits by Network).
 * Endpoint: POST /api/upload/streaming
 * @param files - Array of 4 CSV files (Segments, KPI totals, DMA visits, Network visits)
 * @returns StreamingReportResponse with aggregated metrics
 */
export async function uploadStreamingReports(
  files: File[],
): Promise<StreamingReportResponse> {
  const url = buildApiUrl("/upload/streaming");

  log.info(
    "Uploading MNTN Streaming reports",
    {
      fileNames: files.map((f) => f.name),
      fileCount: files.length,
      totalSizeKb: Number(
        (files.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(2),
      ),
      url,
    },
    "api",
  );

  const { data } = await postMultipart<StreamingReportResponse>({
    url,
    fields: { files },
    onError: (response, errorMessage) => {
      log.error(
        "Streaming upload failed",
        { status: response.status, errorMessage },
        "api",
      );
    },
  });

  log.info(
    "MNTN Streaming reports uploaded and processed successfully",
    {
      recordCount: data.records_processed,
      segmentCount: data.summary.segment_count,
      dmaCount: data.summary.dma_count,
      networkCount: data.summary.network_count,
      totalImpressions: data.summary.total_impressions,
      totalSpend: data.summary.total_spend,
    },
    "api",
  );

  return data;
}

/**
 * Upload Hess Search + Total Sales reports (2 CSV files).
 * Endpoint: POST /api/upload/hess
 */
export async function uploadHessReports(files: File[]): Promise<HessReportResponse> {
  const url = buildApiUrl("/upload/hess");

  log.info(
    "Uploading Hess reports",
    { fileNames: files.map((f) => f.name), fileCount: files.length, url },
    "api",
  );

  if (files.length !== 2) {
    log.error(
      "Hess upload rejected before request",
      { fileCount: files.length },
      "api",
    );
    throw new Error("Hess upload requires exactly 2 CSV files");
  }

  const { data } = await postMultipart<HessReportResponse>({
    url,
    fields: { files },
    onError: (response, errorMessage) => {
      log.error(
        "Hess upload failed",
        { status: response.status, errorMessage },
        "api",
      );
    },
  });

  log.info(
    "Hess reports uploaded and processed successfully",
    {
      recordCount: data.records_processed,
      productCount: data.products.length,
      totalUnits: data.summary.total_units,
      totalAdSpend: data.summary.total_ad_spend,
    },
    "api",
  );

  return data;
}
